(() => {
  function parseMoneyToMinor(text) {
    if (!text) return null;

    try {
      const numeric = String(text)
        .replace(/[^0-9.,]/g, '')
        .replace(/,/g, '');
      const pounds = parseFloat(numeric);
      if (Number.isFinite(pounds)) return Math.round(pounds * 100);
    } catch {}

    return null;
  }

  function getCurrentPriceMinor(product) {
    if (!product) return null;

    let priceEl = product.querySelector(
      '.product-info__price .price .price__current.tpo_product_price'
    );
    if (!priceEl) {
      priceEl = product.querySelector('.product-info__price .price .price__current .js-value');
    }
    if (!priceEl) {
      priceEl = product.querySelector('.product-info__price .price .price__current');
    }
    if (!priceEl) return null;

    return parseMoneyToMinor(priceEl.textContent || '');
  }

  function formatMoney(minor, currency) {
    const amount = Number(minor || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  function updateBlockAmount(block, monthlyMinor) {
    if (!block || typeof monthlyMinor !== 'number') return;

    const amountEl = block.querySelector('[data-short-financing-amount]');
    if (!amountEl) return;

    const currency = block.dataset.currency || 'GBP';
    amountEl.textContent = formatMoney(monthlyMinor, currency);
  }

  function setBlockLoadingState(block, isLoading) {
    if (!block) return;

    block.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  function setBlocksLoadingState(blocks, isLoading) {
    blocks.forEach((block) => setBlockLoadingState(block, isLoading));
  }

  function parsePlansFromJson(block) {
    if (!block) return [];

    const plansId = block.dataset.plansJsonId || '';
    if (!plansId) return [];

    const plansEl = document.getElementById(plansId);
    if (!plansEl) return [];

    try {
      const parsed = JSON.parse(plansEl.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function normalizePlan(rawPlan) {
    if (!rawPlan || typeof rawPlan !== 'object') return null;

    const termMonths = Number(rawPlan.termMonths);
    const aprRaw = rawPlan.aprPercent;
    const aprPercent =
      aprRaw === '' || aprRaw === null || typeof aprRaw === 'undefined' ? null : Number(aprRaw);
    const monthlyRateRaw = rawPlan.monthlyRatePercent;
    const monthlyRatePercent =
      monthlyRateRaw === '' || monthlyRateRaw === null || typeof monthlyRateRaw === 'undefined'
        ? null
        : Number(monthlyRateRaw);
    const loanMinMinor = Number(rawPlan.loanMinMinor);
    const loanMaxMinor = Number(rawPlan.loanMaxMinor);
    const priority = Number(rawPlan.priority);
    const paymentInterval = String(rawPlan.paymentInterval || 'month').toLowerCase();

    if (!Number.isFinite(termMonths) || termMonths <= 0) return null;
    if (!Number.isFinite(loanMinMinor) || !Number.isFinite(loanMaxMinor)) return null;

    const hasValidApr = Number.isFinite(aprPercent) && aprPercent >= 0;
    const hasValidMonthlyRate = Number.isFinite(monthlyRatePercent) && monthlyRatePercent >= 0;
    if (!hasValidApr && !hasValidMonthlyRate) return null;

    return {
      lender: rawPlan.lender || '',
      termMonths,
      aprPercent: hasValidApr ? aprPercent : null,
      monthlyRatePercent: Number.isFinite(monthlyRatePercent) ? monthlyRatePercent : null,
      loanMinMinor: Math.max(0, Math.round(loanMinMinor)),
      loanMaxMinor: Math.max(0, Math.round(loanMaxMinor)),
      paymentInterval,
      defermentPeriod: Number.isFinite(Number(rawPlan.defermentPeriod))
        ? Number(rawPlan.defermentPeriod)
        : 0,
      priority: Number.isFinite(priority) ? priority : 100,
    };
  }

  function calculateMonthlyMinor(amountMinor, plan) {
    if (!Number.isFinite(amountMinor) || !plan || plan.paymentInterval !== 'month') return null;

    const principal = Number(amountMinor) / 100;
    const termMonths = Number(plan.termMonths);
    const aprPercent = Number(plan.aprPercent);
    const monthlyRatePercentRaw = plan.monthlyRatePercent;
    const monthlyRatePercent =
      monthlyRatePercentRaw === '' ||
      monthlyRatePercentRaw === null ||
      typeof monthlyRatePercentRaw === 'undefined'
        ? null
        : Number(monthlyRatePercentRaw);

    if (
      !Number.isFinite(principal) ||
      principal <= 0 ||
      !Number.isFinite(termMonths) ||
      termMonths <= 0
    ) {
      return null;
    }

    // Dopple/Payl8r-style flat monthly rate model:
    // monthly = principal * (1 + monthly_rate * term) / term
    if (Number.isFinite(monthlyRatePercent) && monthlyRatePercent >= 0) {
      const flatFactor = 1 + (monthlyRatePercent / 100) * termMonths;
      const monthlyPayment = (principal * flatFactor) / termMonths;
      if (!Number.isFinite(monthlyPayment) || monthlyPayment <= 0) return null;
      return Math.round(monthlyPayment * 100);
    }

    if (!Number.isFinite(aprPercent)) return null;

    if (aprPercent <= 0) {
      return Math.round((principal / termMonths) * 100);
    }

    // Convert annual APR to effective monthly rate.
    const monthlyRate = Math.pow(1 + aprPercent / 100, 1 / 12) - 1;
    const denominator = 1 - Math.pow(1 + monthlyRate, -termMonths);
    if (!Number.isFinite(denominator) || denominator === 0) return null;

    const monthlyPayment = (principal * monthlyRate) / denominator;
    if (!Number.isFinite(monthlyPayment) || monthlyPayment <= 0) return null;

    return Math.round(monthlyPayment * 100);
  }

  function buildEligibleOffers(amountMinor, plans) {
    if (!Array.isArray(plans) || !plans.length || !Number.isFinite(amountMinor)) return [];

    return plans
      .map((plan) => normalizePlan(plan))
      .filter(Boolean)
      .filter((plan) => amountMinor >= plan.loanMinMinor && amountMinor <= plan.loanMaxMinor)
      .map((plan) => ({ ...plan, monthlyMinor: calculateMonthlyMinor(amountMinor, plan) }))
      .filter((offer) => Number.isFinite(offer.monthlyMinor) && offer.monthlyMinor > 0);
  }

  function pickOffer(offers, preferredInstallments) {
    if (!Array.isArray(offers) || offers.length === 0) return null;

    const sortedOffers = offers
      .slice()
      .sort(
        (a, b) =>
          Number(a.monthlyMinor) - Number(b.monthlyMinor) ||
          Number(a.priority) - Number(b.priority) ||
          Number(a.termMonths) - Number(b.termMonths)
      );

    if (Number.isFinite(preferredInstallments) && preferredInstallments > 0) {
      const preferred = sortedOffers.find(
        (offer) => Number(offer.termMonths) === preferredInstallments
      );
      if (preferred) return preferred;
    }

    return sortedOffers[0] || null;
  }

  function initForProduct(product) {
    if (!product) return;
    if (product.dataset.shortFinancingInitialized === 'true') return;

    const blocks = product.querySelectorAll('[data-short-financing]');
    if (!blocks.length) return;

    product.dataset.shortFinancingInitialized = 'true';

    const updateAll = (amountMinor) => {
      if (!Number.isFinite(amountMinor)) return;

      const loadingStart = Date.now();
      setBlocksLoadingState(blocks, true);
      blocks.forEach((block) => {
        const blockPlans = block.shortFinancingPlans || [];
        const preferredInstallments = parseInt(block.dataset.installments || '', 10);
        const offers = buildEligibleOffers(amountMinor, blockPlans);
        const selectedOffer = pickOffer(offers, preferredInstallments);
        if (selectedOffer) {
          updateBlockAmount(block, selectedOffer.monthlyMinor);
        }
      });

      const minimumLoadingMs = 120;
      const elapsedMs = Date.now() - loadingStart;
      const releaseMs = Math.max(0, minimumLoadingMs - elapsedMs);
      window.setTimeout(() => setBlocksLoadingState(blocks, false), releaseMs);
    };

    blocks.forEach((block) => {
      block.shortFinancingPlans = parsePlansFromJson(block);
    });

    let initialAmountMinor = getCurrentPriceMinor(product);
    if (typeof initialAmountMinor !== 'number') {
      const fallbackMinor = parseInt(blocks[0].dataset.initialPriceCents || '', 10);
      initialAmountMinor = Number.isFinite(fallbackMinor) ? fallbackMinor : null;
    }
    if (typeof initialAmountMinor === 'number') {
      updateAll(initialAmountMinor);
    }

    product.addEventListener('on:product-price:change', (event) => {
      const detail = event && event.detail ? event.detail : {};
      const nextAmountMinor = Number(detail.amountMinor ?? detail.amount);
      if (Number.isFinite(nextAmountMinor)) {
        updateAll(nextAmountMinor);
      }
    });
  }

  function init() {
    document.querySelectorAll('.product').forEach((product) => initForProduct(product));
  }

  document.addEventListener('DOMContentLoaded', init, { once: true });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('on:bfcache:load-restore', init, { once: true });
})();
